/**
 * Pure geometry helpers for editing shapes AFTER creation — the math
 * behind drag-to-move, endpoint repositioning, and Transformer
 * resize commits. Extracted from the inline dragEnd handlers in
 * `ShapeRenderer.tsx` so both canvas surfaces (tactical board +
 * video telestrator) share one unit-tested implementation.
 *
 * Everything here works in normalized [0, 1] unit coordinates except
 * `unitRectFromNodeBox`, which converts a Konva node's pixel box back
 * to a unit rect at transform-end time.
 */

import { clamp01, type Dims, type Point } from "./coords";
import {
  SHARED_MIN_ZONE_SIZE,
  type SharedArrowShape,
  type SharedLineShape,
  type SharedShape,
} from "./shared-shape";

/** Translate a shared shape by a unit-coordinate delta, clamping every
 * coordinate back into [0, 1]. Zones keep their minimum size and are
 * trimmed to fit the surface — dragging a zone into the right/bottom
 * edge must not shrink it below the 0.02 floor that both
 * `clampSharedShape` and the Transformer resize enforce. */
export function translateSharedShape(
  shape: SharedShape,
  dx: number,
  dy: number,
): SharedShape {
  switch (shape.kind) {
    case "arrow":
    case "line":
      return {
        ...shape,
        x1: clamp01(shape.x1 + dx),
        y1: clamp01(shape.y1 + dy),
        x2: clamp01(shape.x2 + dx),
        y2: clamp01(shape.y2 + dy),
      };
    case "zone": {
      const x = clamp01(Math.min(shape.x + dx, 1 - SHARED_MIN_ZONE_SIZE));
      const y = clamp01(Math.min(shape.y + dy, 1 - SHARED_MIN_ZONE_SIZE));
      return {
        ...shape,
        x,
        y,
        w: Math.min(Math.max(shape.w, SHARED_MIN_ZONE_SIZE), 1 - x),
        h: Math.min(Math.max(shape.h, SHARED_MIN_ZONE_SIZE), 1 - y),
      };
    }
    case "label":
      return { ...shape, x: clamp01(shape.x + dx), y: clamp01(shape.y + dy) };
    case "freehand":
      return { ...shape, points: translatePoints(shape.points, dx, dy) };
  }
}

/** Translate a point list by a unit delta, clamping each point. Used
 * by freehand drags and the telestrator formation's whole-shape drag
 * (anchors + hull points move together). */
export function translatePoints(points: Point[], dx: number, dy: number): Point[] {
  return points.map((p) => ({ x: clamp01(p.x + dx), y: clamp01(p.y + dy) }));
}

export type EndpointEnd = "start" | "end";

/** Replace ONE endpoint of an arrow/line, leaving the other end
 * untouched. `start` is (x1, y1); `end` is (x2, y2) — the arrowhead
 * end for arrows. */
export function withEndpoint<T extends SharedArrowShape | SharedLineShape>(
  shape: T,
  end: EndpointEnd,
  pt: Point,
): T {
  const x = clamp01(pt.x);
  const y = clamp01(pt.y);
  return end === "start" ? { ...shape, x1: x, y1: y } : { ...shape, x2: x, y2: y };
}

/** Convert a Konva node's pixel box (post-transform x/y/width/height,
 * scale already folded in by the caller) back to a clamped unit rect.
 * Shared by both editors' `handleTransformEnd` for every box-bounded
 * kind (zone, telestrator circle, spotlight). Enforces the same 0.02
 * minimum + fit-in-bounds rules as `clampSharedShape`. */
export function unitRectFromNodeBox(
  box: { x: number; y: number; width: number; height: number },
  dims: Dims,
): { x: number; y: number; w: number; h: number } {
  const x = clamp01(box.x / dims.width);
  const y = clamp01(box.y / dims.height);
  const w = Math.max(
    SHARED_MIN_ZONE_SIZE,
    Math.min(1 - x, box.width / dims.width),
  );
  const h = Math.max(
    SHARED_MIN_ZONE_SIZE,
    Math.min(1 - y, box.height / dims.height),
  );
  return { x, y, w, h };
}
